import { defineStore } from 'pinia';
import { useBreakpoints } from '@vueuse/core';
import { axios } from '@services/AxiosBootstrap';
import countries from '@stores/Countries';

// Parse the JSON config from the script tag
const configScript = document.getElementById('frontend-config');
const config = JSON.parse(configScript.textContent);
const getPlatform = () => {
    let userAgent = window.navigator.userAgent,
        platform = window.navigator.platform,
        macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
        windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
        iosPlatforms = ['iPhone', 'iPad', 'iPod'],
        os = null;

    if (macosPlatforms.indexOf(platform) !== -1) {
        os = 'Mac OS';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = 'Windows';
    } else if (/Android/.test(userAgent)) {
        os = 'Android';
    } else if (!os && /Linux/.test(platform)) {
        os = 'Linux';
    }

    return os;
};

export const useAppStore = defineStore('app-store', {
    state: () => {
        const breakpoints = useBreakpoints({
            sm: 0, // optional
            md: 768,
            lg: 1024,
        });

        return {
            platform: getPlatform(),
            breakpoints,
            currency: 'SEK',
            locale: 'sv-SE',
            burgerMenuOpen: false,
            basePath: 'https://localhost:3000/',
            maximumFractionDigits: 0,
            tokenDomain: null,
            navigation: {
                main: [],
                footer: [],
            },
            storefrontContext: null,
            pageClasses: new Set([]),
            geoCountry: null,
            theme: 'default',
            ...config
        };
    },
    getters: {
        isDesktop(state) {
            return state.breakpoints.greater(() => 'md');
        },
        isMobile(state) {
            return state.breakpoints.smaller(() => 'md');
        },
        isTablet(state) {
            return state.breakpoints.active() === 'md';
        },
        theme(state) {
            return state.theme;
        }
    },
    actions: {
        resetTheme() {
            this.setTheme('default');
        },
        setThemeVariable(target, theme) {
            document.documentElement.style.setProperty(`--theme-${target}`, `var(--${theme}-${target})`);
        },
        setTheme(string) {
            let theme = 'theme-default';

            if ('christmas' === string) {
                theme = 'christmas';
            }

            if ('wedding' === string) {
                theme = 'wedding';
            }

            if ('black-week' === string) {
                theme = 'black-week';
            }

            this.$state.theme = theme;
            this.setThemeVariable('bg', theme);
            this.setThemeVariable('title-color', theme);
            this.setThemeVariable('body-color', theme);
            this.setThemeVariable('button-bg', theme);
            this.setThemeVariable('button-color', theme);
            this.setThemeVariable('body-color', theme);
            this.setThemeVariable('decoration', theme);
            this.setThemeVariable('decoration-footer', theme);
        },
        isDevelopment() {
            return ['localhost', 'stage', '.int', '.local'].some(term => location.href.includes(term));
        },
        warmUp() {
            this.getGeoCountry().then((country) => {
                if (country) {
                    this.geoCountry  = country;
                }
            });
        },
        setLocale(locale) {
            this.$i18n.locale = locale;
        },
        getFormatterInstance(currency = null) {
            return new Intl.NumberFormat(this.$state.locale, {
                style: 'currency',
                currency: currency || this.$state.currency,
                maximumFractionDigits: this.$state.maximumFractionDigits,
            });
        },
        async getGeoCountry() {
            if (this.geoCountry) {
                return this.geoCountry;
            }

            try {
                const response = await axios.get('/api/store/countries');
                return response?.data?.geo;
            } catch (e) {
                return null;
            }
        },
        getCountries() {
            return countries;
        },
        getBreakpoints() {
            return this.$state.breakpoints;
        },
        formatPrice(price, currency) {
            if (currency) {
                return this.getFormatterInstance(currency).format(price)
            }
            return this.getFormatterInstance().format(price);
        },
        simplifyUrl(url) {
            const parsedUrl = new URL(url);
            return parsedUrl.hostname;
        },
        formatLink(link, item) {
            const clean =  link?.replace(/^\//, '');

            if (clean === undefined) {
                console.error('undefined link', link, item);
            }

            return `${this.$state.basePath}${clean}`;
        },
        getNavLink(item) {
            let suffix = '';

            if (item.type === 'link') {
                if (item.linkType === 'external') {
                    return item.externalLink;
                } else {
                    suffix = item.internalLink;
                }

                return this.formatLink(suffix, item);
            }


            if (item?.seoUrls?.length > 0) {
                suffix = item.seoUrls[0]?.seoPathInfo;
            } else {
                suffix = this.formatLink('/navigation/' + item.id);
            }

            return this.formatLink(suffix, item);
        },
        getMainNavigation() {
            return this.navigation.main.map(item => {
                item.normalizedhref = this.getNavLink(item);
                item.name = item.translated.name;
                return item;
            }).sort((a, b) => a?.customFields?.category_order - b?.customFields?.category_order);
        },
        getFooterNavigation() {
            return this.navigation.footer.map(item => {
                item.normalizedhref = this.getNavLink(item);
                item.name = item.translated.name;
                return item;
            }).sort((a, b) => a?.customFields?.category_order - b?.customFields?.category_order);
        },
        getSalesChannel() {
            return this.storefrontContext?.salesChannel;
        },
        getSalesChannelId() {
            return this.storefrontContext?.salesChannel?.id;
        },
        getSalesChannelDomains() {
            return this.getSalesChannel()?.domains;
        },
        getSalesChannelUrl() {
            return this.basePath;
        },
        getPageClasses() {
            return this.pageClasses;
        },
    },
});
